import { useState, useEffect } from "react";
import { 
  Plus, 
  ArrowRight, 
  Pencil, 
  Trash2, 
  Power, 
  PowerOff,
  ArrowRightLeft,
  Server
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import api from "../services/api";
import { toast } from "sonner";

export default function Flows() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    flow_type: "rest_to_soap",
    source_endpoint: "",
    destination_endpoint: "",
    description: "",
    is_active: true
  });

  const fetchFlows = async () => {
    try {
      const res = await api.get("/flows");
      setFlows(res.data);
    } catch (error) {
      toast.error("Erro ao carregar fluxos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFlows();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedFlow) {
        await api.put(`/flows/${selectedFlow.id}`, formData);
        toast.success("Fluxo atualizado com sucesso");
      } else {
        await api.post("/flows", formData);
        toast.success("Fluxo criado com sucesso");
      }
      setDialogOpen(false);
      fetchFlows();
      resetForm();
    } catch (error) {
      toast.error("Erro ao salvar fluxo");
    }
  };

  const handleToggle = async (flow) => {
    try {
      await api.patch(`/flows/${flow.id}/toggle`);
      toast.success(`Fluxo ${flow.is_active ? 'desativado' : 'ativado'}`);
      fetchFlows();
    } catch (error) {
      toast.error("Erro ao alternar status do fluxo");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/flows/${selectedFlow.id}`);
      toast.success("Fluxo excluído com sucesso");
      setDeleteDialogOpen(false);
      setSelectedFlow(null);
      fetchFlows();
    } catch (error) {
      toast.error("Erro ao excluir fluxo");
    }
  };

  const openEditDialog = (flow) => {
    setSelectedFlow(flow);
    setFormData({
      name: flow.name,
      flow_type: flow.flow_type,
      source_endpoint: flow.source_endpoint,
      destination_endpoint: flow.destination_endpoint,
      description: flow.description || "",
      is_active: flow.is_active
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (flow) => {
    setSelectedFlow(flow);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedFlow(null);
    setFormData({
      name: "",
      flow_type: "rest_to_soap",
      source_endpoint: "",
      destination_endpoint: "",
      description: "",
      is_active: true
    });
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="flows-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Fluxos de Integração</h1>
          <p className="text-sm text-zinc-500">Gerencie as transformações REST ↔ SOAP</p>
        </div>
        <Button 
          onClick={openNewDialog}
          className="gap-2 bg-cyan-500 hover:bg-cyan-600 text-black font-medium btn-glow"
          data-testid="new-flow-btn"
        >
          <Plus className="w-4 h-4" />
          Novo Fluxo
        </Button>
      </div>

      {/* Flows Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800 animate-pulse">
              <CardContent className="h-48" />
            </Card>
          ))}
        </div>
      ) : flows.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {flows.map((flow) => (
            <Card 
              key={flow.id} 
              className={`bg-zinc-900/50 border-zinc-800 card-hover ${!flow.is_active && 'opacity-60'}`}
              data-testid={`flow-card-${flow.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <ArrowRightLeft className={`w-4 h-4 ${flow.is_active ? 'text-cyan-400' : 'text-zinc-500'}`} />
                    <CardTitle className="text-base font-mono text-white">{flow.name}</CardTitle>
                  </div>
                  <Badge className={flow.is_active ? "bg-green-500/20 text-green-400" : "bg-zinc-500/20 text-zinc-400"}>
                    {flow.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Flow Type */}
                <div className="flex items-center gap-2 text-sm">
                  <Badge className={flow.flow_type === "rest_to_soap" ? "badge-rest" : "badge-soap"}>
                    {flow.flow_type === "rest_to_soap" ? "REST" : "SOAP"}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-zinc-500" />
                  <Badge className={flow.flow_type === "rest_to_soap" ? "badge-soap" : "badge-rest"}>
                    {flow.flow_type === "rest_to_soap" ? "SOAP" : "REST"}
                  </Badge>
                </div>

                {/* Endpoints */}
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Server className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-500">Origem:</span>
                    <span className="text-zinc-300 truncate font-mono">{flow.source_endpoint || "-"}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Server className="w-3 h-3 text-zinc-500" />
                    <span className="text-zinc-500">Destino:</span>
                    <span className="text-zinc-300 truncate font-mono">{flow.destination_endpoint || "-"}</span>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-zinc-500 pt-2 border-t border-zinc-800">
                  <span>Chamadas: {flow.stats?.total_calls || 0}</span>
                  <span className="text-green-400">✓ {flow.stats?.success || 0}</span>
                  <span className="text-red-400">✗ {flow.stats?.errors || 0}</span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(flow)}
                    data-testid={`edit-flow-${flow.id}`}
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    Editar
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleToggle(flow)}
                    data-testid={`toggle-flow-${flow.id}`}
                  >
                    {flow.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    className="text-red-400 hover:text-red-300 hover:border-red-400"
                    onClick={() => openDeleteDialog(flow)}
                    data-testid={`delete-flow-${flow.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ArrowRightLeft className="w-16 h-16 text-zinc-700 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Nenhum fluxo configurado</h3>
            <p className="text-sm text-zinc-500 mb-4">Crie seu primeiro fluxo de integração REST ↔ SOAP</p>
            <Button 
              onClick={openNewDialog}
              className="bg-cyan-500 hover:bg-cyan-600 text-black"
              data-testid="empty-new-flow-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Criar Fluxo
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono text-white">
              {selectedFlow ? "Editar Fluxo" : "Novo Fluxo de Integração"}
            </DialogTitle>
            <DialogDescription>
              Configure a transformação entre REST e SOAP
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome do Fluxo</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Pesagem → ERP"
                className="bg-zinc-800 border-zinc-700"
                required
                data-testid="flow-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="flow_type">Tipo de Fluxo</Label>
              <Select 
                value={formData.flow_type} 
                onValueChange={(v) => setFormData({ ...formData, flow_type: v })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="flow-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="rest_to_soap">REST → SOAP</SelectItem>
                  <SelectItem value="soap_to_rest">SOAP → REST</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="source_endpoint">Endpoint de Origem</Label>
              <Input
                id="source_endpoint"
                value={formData.source_endpoint}
                onChange={(e) => setFormData({ ...formData, source_endpoint: e.target.value })}
                placeholder="Ex: /api/webhook/rodopeso"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="flow-source-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="destination_endpoint">Endpoint de Destino</Label>
              <Input
                id="destination_endpoint"
                value={formData.destination_endpoint}
                onChange={(e) => setFormData({ ...formData, destination_endpoint: e.target.value })}
                placeholder="Ex: https://senior.com/services/pesagem"
                className="bg-zinc-800 border-zinc-700 font-mono text-sm"
                data-testid="flow-destination-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição (opcional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descreva o propósito deste fluxo..."
                className="bg-zinc-800 border-zinc-700"
                rows={3}
                data-testid="flow-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                className="bg-cyan-500 hover:bg-cyan-600 text-black"
                data-testid="flow-submit-btn"
              >
                {selectedFlow ? "Salvar Alterações" : "Criar Fluxo"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir o fluxo "{selectedFlow?.name}"? 
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
